import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Lock, Copy, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "../lib/clipboard";
import { api } from "../lib/api";

const AVATARS = [
  { id: "pink-w",   img: "/assets/home/avatar-pink-w.png",   ring: "#FF2E9A" },
  { id: "orange-w", img: "/assets/home/avatar-orange-w.png", ring: "#FFE082" },
  { id: "purple-w", img: "/assets/home/avatar-purple-w.png", ring: "#9B6BFF" },
  { id: "red-m",    img: "/assets/home/avatar-red-m.png",    ring: "#FF8A00" },
  { id: "blue-m",   img: "/assets/home/avatar-blue-m.png",   ring: "#3DA4FF" },
  { id: "green-m",  img: "/assets/home/avatar-green-m.png",  ring: "#3DD68C" },
];

/**
 * Group Mode join — minimal, fast.
 * Partner arrives via /group-join/VIBE-XXXX → name + avatar → join → enters Squad lobby.
 */
export default function PlayGroupJoin({ code, onBack, onJoined }) {
  const [name, setName] = useState(localStorage.getItem("player_name") || "");
  const [avatarId, setAvatarId] = useState(localStorage.getItem("group_avatar_id") || AVATARS[2].id);
  const [hostName, setHostName] = useState("the host");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getGroupRoom(code).then((r) => {
      if (!alive) return;
      const host = (r.players || []).find((p) => p.role === "host");
      setHostName(host?.name || "the host");
    }).catch(() => { /* show generic title */ });
    return () => { alive = false; };
  }, [code]);

  const handleJoin = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error("What should they call you?");
      return;
    }
    setLoading(true);
    try {
      await api.joinGroupRoom(code, { name: name.trim(), avatar_id: avatarId });
      localStorage.setItem("player_name", name.trim());
      localStorage.setItem("group_avatar_id", avatarId);
      onJoined?.(code);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't join room");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    const ok = await copyText(code || "");
    if (ok) toast.success(`Room code ${code} copied`);
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="group-join-screen">
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 40, left: "50%", transform: "translateX(-50%)",
          width: 480, height: 480,
          background: "radial-gradient(circle, rgba(255, 138, 0, 0.32) 0%, rgba(255, 184, 0, 0.14) 38%, transparent 70%)",
          filter: "blur(50px)", zIndex: 0,
        }}
        aria-hidden
      />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="group-join-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div
            className="px-3 py-1 rounded-full text-[10px] font-black tracking-[0.22em]"
            style={{
              background: "rgba(255, 138, 0, 0.10)",
              border: "1.5px solid #FF8A00",
              color: "#FFE082",
              boxShadow: "0 0 10px rgba(255, 138, 0, 0.45)",
            }}
          >
            🎉 GROUP MODE
          </div>
          <div className="w-10" aria-hidden />
        </div>

        <div className="text-center mb-4 mt-3">
          <h1
            className="font-extrabold leading-[0.95] tracking-tight"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(1.95rem, 8.2vw, 2.5rem)",
              letterSpacing: "-0.025em",
              background: "linear-gradient(90deg, #FFF3C2 0%, #FFB800 55%, #FF8A00 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0 0 1px rgba(255, 184, 0, 0.6)",
              filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
            }}
            data-testid="group-join-title"
          >
            {hostName} invited you 🎉
          </h1>
          <p className="text-white/75 text-[14px] font-medium mt-1.5">
            Join the squad and start the chaos 👀
          </p>
        </div>

        <form onSubmit={handleJoin} className="space-y-4">
          <div>
            <label className="block text-[10.5px] font-black tracking-[0.22em] text-[#FFE082] mb-2 pl-1">
              YOUR NAME
            </label>
            <div className="relative">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                maxLength={20}
                autoFocus
                data-testid="group-join-name-input"
                className="w-full rounded-2xl px-5 py-[15px] pr-12 text-white text-[16px] font-bold placeholder:text-white/35 focus:outline-none"
                style={{
                  background: "rgba(15, 8, 22, 0.55)",
                  border: "2px solid #FF8A00",
                  boxShadow: "0 0 14px rgba(255, 138, 0, 0.45)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              />
              <UserPlus
                className="absolute right-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                style={{ color: "#FFE082", filter: "drop-shadow(0 0 5px #FFE082)" }}
                strokeWidth={2.2}
              />
            </div>
          </div>

          <div>
            <div className="block text-[10.5px] font-black tracking-[0.22em] text-[#FFE082] mb-2 pl-1">
              YOUR LOOK
            </div>
            <div className="grid grid-cols-6 gap-2" data-testid="group-join-avatars">
              {AVATARS.map((a) => {
                const sel = avatarId === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAvatarId(a.id)}
                    data-testid={`group-join-avatar-${a.id}`}
                    className="aspect-square rounded-full overflow-hidden transition active:scale-95"
                    style={{
                      border: `2.5px solid ${sel ? a.ring : "rgba(255, 255, 255, 0.10)"}`,
                      boxShadow: sel ? `0 0 14px ${a.ring}aa, inset 0 0 6px ${a.ring}33` : "none",
                    }}
                  >
                    <img src={a.img} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="block text-[10.5px] font-black tracking-[0.22em] text-[#FFE082] mb-2 pl-1">
              ROOM CODE
            </div>
            <button
              type="button"
              onClick={copyCode}
              data-testid="group-join-code-pill"
              className="w-full rounded-full flex items-center justify-between px-5 py-3 transition active:scale-[0.99]"
              style={{
                background: "rgba(15, 8, 22, 0.55)",
                border: "2px solid #FF8A00",
                boxShadow: "0 0 14px rgba(255, 138, 0, 0.45)",
              }}
            >
              <Lock className="w-[18px] h-[18px]" style={{ color: "#FFE082" }} strokeWidth={2.2} />
              <span
                className="text-white font-extrabold flex-1 text-center"
                style={{ fontFamily: "'Manrope', sans-serif", fontSize: "19px", letterSpacing: "0.10em" }}
              >
                {code || "------"}
              </span>
              <Copy className="w-[18px] h-[18px]" style={{ color: "#FFE082" }} strokeWidth={2.2} />
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="group-join-submit"
            className="vc-cta vc-cta-primary w-full rounded-full py-[18px] font-extrabold flex items-center justify-center gap-2 mt-3"
            style={{ fontSize: "17px" }}
          >
            {loading ? "Joining…" : (<>Join the squad <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
          </button>
        </form>
      </main>
    </div>
  );
}
